import { useState, useMemo } from 'react';
import { Pagination, Skeleton, Tabs, Input } from 'antd';
import { EmptyCase, Screen } from '../../components';
import { useGetFailedQuestions, useGetSavedQuestions } from '../../services/userQuestion';
import { type IQuestion } from '../../types/question';
import { type ISubject } from '../../types/subject';
import { IconBookmark, IconX, IconCheck, IconBook, IconMessageCircle, IconSearch } from '@tabler/icons-react';
import clsx from 'clsx';

interface QuestionCardProps {
  question: IQuestion
  comment?: string
  type: 'saved' | 'failed'
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, comment, type }) => {
  const subject = question.subject as ISubject;

  return (
    <div className={clsx(
      'rounded-xl border-2 transition-all duration-300',
      'bg-white border-gray-200 shadow-sm',
      'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
      'hover:shadow-md hover:scale-[1.01]',
      'overflow-hidden'
    )}>
      {/* Colored bar on top */}
      <div
        className="h-1.5 w-full"
        style={{ backgroundColor: subject.color }}
      />

      <div className="p-4">
        {/* Header with subject */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: subject.color }}
            />
            <span className={clsx(
              'text-xs font-semibold uppercase tracking-wide',
              'text-gray-700 dark:text-gray-300'
            )}>
              {subject.name}
            </span>
          </div>
          <div className={clsx(
            'px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5',
            type === 'saved'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          )}>
            {type === 'saved' ? (
              <>
                <IconBookmark size={12} />
                <span>Guardada</span>
              </>
            ) : (
              <>
                <IconX size={12} />
                <span>Fallida</span>
              </>
            )}
          </div>
        </div>

        {/* Question */}
        <div className="mb-4">
          <div className="flex items-start gap-2">
            <IconBook size={18} className={clsx(
              'mt-0.5 flex-shrink-0',
              'text-gray-400 dark:text-surface-light'
            )} />
            <h4 className={clsx(
              'font-medium text-base leading-relaxed',
              'text-gray-900 dark:text-white'
            )}>
              {question.title}
            </h4>
          </div>
        </div>

        {/* Correct answer */}
        <div className={clsx(
          'p-3 rounded-lg border-l-4 mb-3',
          'bg-green-50 dark:bg-green-900/20',
          'border-green-500 dark:border-green-400'
        )}>
          <div className="flex items-start gap-2">
            <IconCheck size={18} className={clsx(
              'mt-0.5 flex-shrink-0',
              'text-green-600 dark:text-green-400'
            )} />
            <div className="flex-1">
              <span className={clsx(
                'text-xs font-semibold uppercase tracking-wide mb-1 block',
                'text-green-700 dark:text-green-300'
              )}>
                Respuesta correcta:
              </span>
              <p className={clsx(
                'text-sm font-medium',
                'text-green-800 dark:text-green-200'
              )}>
                {question.correctAnswer}
              </p>
            </div>
          </div>
        </div>

        {/* Comment (only for saved questions) */}
        {comment && comment !== '-' && (
          <div className={clsx(
            'p-3 rounded-lg border-l-4',
            'bg-blue-50 dark:bg-blue-900/20',
            'border-blue-500 dark:border-blue-400'
          )}>
            <div className="flex items-start gap-2">
              <IconMessageCircle size={18} className={clsx(
                'mt-0.5 flex-shrink-0',
                'text-blue-600 dark:text-blue-400'
              )} />
              <div className="flex-1">
                <span className={clsx(
                  'text-xs font-semibold uppercase tracking-wide mb-1 block',
                  'text-blue-700 dark:text-blue-300'
                )}>
                  Mi comentario:
                </span>
                <p className={clsx(
                  'text-sm font-medium',
                  'text-blue-800 dark:text-blue-200'
                )}>
                  {comment}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SavedQuestionsPage = () => {
  const { questions: failedQuestions, loading: loadingFailed } = useGetFailedQuestions();
  const { questions: savedQuestions, loading: loadingSaved } = useGetSavedQuestions();
  const [activeTab, setActiveTab] = useState<'saved' | 'failed'>('saved');
  const [currentPageSaved, setCurrentPageSaved] = useState(1);
  const [currentPageFailed, setCurrentPageFailed] = useState(1);
  const [searchQuerySaved, setSearchQuerySaved] = useState('');
  const [searchQueryFailed, setSearchQueryFailed] = useState('');
  const pageSize = 6;

  // Filter function
  const filterQuestions = (
    questions: Array<{ _id: string, question: IQuestion, comment?: string }> | undefined,
    searchQuery: string
  ): Array<{ _id: string, question: IQuestion, comment?: string }> => {
    if (!searchQuery.trim()) return questions || [];

    const query = searchQuery.toLowerCase().trim();
    return (questions || []).filter((item) => {
      const question = item.question;
      const subject = question.subject as ISubject;
      const comment = item.comment || '';

      return (
        question.title.toLowerCase().includes(query) ||
        question.correctAnswer.toLowerCase().includes(query) ||
        subject.name.toLowerCase().includes(query) ||
        comment.toLowerCase().includes(query)
      );
    });
  };

  // Filtered questions
  const filteredSavedQuestions = useMemo(() => {
    return filterQuestions(savedQuestions, searchQuerySaved);
  }, [savedQuestions, searchQuerySaved]);

  const filteredFailedQuestions = useMemo(() => {
    return filterQuestions(failedQuestions as any, searchQueryFailed);
  }, [failedQuestions, searchQueryFailed]);

  // Pagination for saved questions
  const savedPagination = useMemo(() => {
    const start = (currentPageSaved - 1) * pageSize;
    const end = start + pageSize;
    return {
      data: filteredSavedQuestions.slice(start, end),
      total: filteredSavedQuestions.length
    };
  }, [filteredSavedQuestions, currentPageSaved, pageSize]);

  // Pagination for failed questions
  const failedPagination = useMemo(() => {
    const start = (currentPageFailed - 1) * pageSize;
    const end = start + pageSize;
    return {
      data: filteredFailedQuestions.slice(start, end),
      total: filteredFailedQuestions.length
    };
  }, [filteredFailedQuestions, currentPageFailed, pageSize]);

  // Reset pagination when search changes
  const handleSearchSaved = (value: string) => {
    setSearchQuerySaved(value);
    setCurrentPageSaved(1);
  };

  const handleSearchFailed = (value: string) => {
    setSearchQueryFailed(value);
    setCurrentPageFailed(1);
  };

  const loading = loadingFailed || loadingSaved;

  const tabItems = [
    {
      key: 'saved',
      label: (
        <div className="flex items-center gap-2">
          <IconBookmark size={18} />
          <span>Preguntas guardadas</span>
          {savedQuestions && savedQuestions.length > 0 && (
            <span className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-semibold',
              'bg-blue-100 text-blue-700',
              'dark:bg-blue-900/40 dark:text-blue-300'
            )}>
              {savedQuestions.length}
            </span>
          )}
        </div>
      ),
      children: (
        <div className="space-y-6">
          {/* Search bar */}
          <Input
            placeholder="Buscar por título, materia, respuesta o comentario..."
            prefix={<IconSearch className="text-gray-400" />}
            value={searchQuerySaved}
            onChange={(e) => handleSearchSaved(e.target.value)}
            allowClear
            size="large"
            className={clsx(
              'rounded-lg',
              '[&_.ant-input]:bg-white',
              '[&_.ant-input]:dark:bg-surface-dark',
              '[&_.ant-input]:border-gray-300',
              '[&_.ant-input]:dark:border-borders',
              '[&_.ant-input]:text-gray-900',
              '[&_.ant-input]:dark:text-white',
              '[&_.ant-input]:placeholder:text-gray-400',
              '[&_.ant-input]:dark:placeholder:text-gray-500',
              '[&_.ant-input]:focus:border-blue-500',
              '[&_.ant-input]:dark:focus:border-blue-400',
              '[&_.ant-input]:focus:shadow-md',
              '[&_.ant-input]:dark:focus:shadow-blue-500/20'
            )}
          />

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} active paragraph={{ rows: 4 }} />
              ))}
            </div>
          ) : savedPagination.data.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedPagination.data.map((item) => (
                  <QuestionCard
                    key={item._id}
                    question={item.question}
                    comment={item.comment}
                    type="saved"
                  />
                ))}
              </div>
              {savedPagination.total > pageSize && (
                <div className="flex justify-center pt-4 pb-6">
                  <Pagination
                    current={currentPageSaved}
                    total={savedPagination.total}
                    pageSize={pageSize}
                    onChange={setCurrentPageSaved}
                    showSizeChanger={false}
                    showTotal={(total) => `Total: ${total} preguntas`}
                    className={clsx(
                      '[&_.ant-pagination-item]:transition-all',
                      '[&_.ant-pagination-item]:border-gray-300',
                      '[&_.ant-pagination-item]:dark:border-gray-600',
                      '[&_.ant-pagination-item_a]:text-gray-700',
                      '[&_.ant-pagination-item_a]:dark:text-gray-300',
                      // Active page - more prominent
                      '[&_.ant-pagination-item-active]:!bg-blue-600',
                      '[&_.ant-pagination-item-active]:!border-blue-600',
                      '[&_.ant-pagination-item-active_a]:!text-white',
                      '[&_.ant-pagination-item-active]:shadow-md',
                      '[&_.ant-pagination-item-active]:shadow-blue-500/30',
                      '[&_.ant-pagination-item-active]:dark:!bg-blue-500',
                      '[&_.ant-pagination-item-active]:dark:!border-blue-500',
                      '[&_.ant-pagination-item-active]:dark:shadow-blue-500/20',
                      // Hover states
                      '[&_.ant-pagination-item:hover]:border-blue-400',
                      '[&_.ant-pagination-item:hover]:dark:border-blue-500',
                      '[&_.ant-pagination-item:hover_a]:text-blue-600',
                      '[&_.ant-pagination-item:hover_a]:dark:text-blue-400',
                      // Navigation buttons
                      '[&_.ant-pagination-prev]:text-gray-700',
                      '[&_.ant-pagination-prev]:dark:text-gray-300',
                      '[&_.ant-pagination-next]:text-gray-700',
                      '[&_.ant-pagination-next]:dark:text-gray-300',
                      '[&_.ant-pagination-prev:hover]:text-blue-600',
                      '[&_.ant-pagination-prev:hover]:dark:text-blue-400',
                      '[&_.ant-pagination-next:hover]:text-blue-600',
                      '[&_.ant-pagination-next:hover]:dark:text-blue-400'
                    )}
                  />
                </div>
              )}
            </>
          ) : searchQuerySaved ? (
            <EmptyCase text="No se encontraron preguntas con ese criterio de búsqueda" />
          ) : (
            <EmptyCase text="No hay preguntas guardadas" />
          )}
        </div>
      )
    },
    {
      key: 'failed',
      label: (
        <div className="flex items-center gap-2">
          <IconX size={18} />
          <span>Preguntas fallidas</span>
          {failedQuestions && failedQuestions.length > 0 && (
            <span className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-semibold',
              'bg-red-100 text-red-700',
              'dark:bg-red-900/40 dark:text-red-300'
            )}>
              {failedQuestions.length}
            </span>
          )}
        </div>
      ),
      children: (
        <div className="space-y-6">
          {/* Search bar */}
          <Input
            placeholder="Buscar por título, materia o respuesta..."
            prefix={<IconSearch className="text-gray-400" />}
            value={searchQueryFailed}
            onChange={(e) => handleSearchFailed(e.target.value)}
            allowClear
            size="large"
            className={clsx(
              'rounded-lg',
              '[&_.ant-input]:bg-white',
              '[&_.ant-input]:dark:bg-surface-dark',
              '[&_.ant-input]:border-gray-300',
              '[&_.ant-input]:dark:border-borders',
              '[&_.ant-input]:text-gray-900',
              '[&_.ant-input]:dark:text-white',
              '[&_.ant-input]:placeholder:text-gray-400',
              '[&_.ant-input]:dark:placeholder:text-gray-500',
              '[&_.ant-input]:focus:border-red-500',
              '[&_.ant-input]:dark:focus:border-red-400',
              '[&_.ant-input]:focus:shadow-md',
              '[&_.ant-input]:dark:focus:shadow-red-500/20'
            )}
          />

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} active paragraph={{ rows: 4 }} />
              ))}
            </div>
          ) : failedPagination.data.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {failedPagination.data.map((item) => (
                  <QuestionCard
                    key={item._id}
                    question={item.question}
                    type="failed"
                  />
                ))}
              </div>
              {failedPagination.total > pageSize && (
                <div className="flex justify-center pt-4 pb-6">
                  <Pagination
                    current={currentPageFailed}
                    total={failedPagination.total}
                    pageSize={pageSize}
                    onChange={setCurrentPageFailed}
                    showSizeChanger={false}
                    showTotal={(total) => `Total: ${total} preguntas`}
                    className={clsx(
                      '[&_.ant-pagination-item]:transition-all',
                      '[&_.ant-pagination-item]:border-gray-300',
                      '[&_.ant-pagination-item]:dark:border-gray-600',
                      '[&_.ant-pagination-item_a]:text-gray-700',
                      '[&_.ant-pagination-item_a]:dark:text-gray-300',
                      // Active page - more prominent
                      '[&_.ant-pagination-item-active]:!bg-red-600',
                      '[&_.ant-pagination-item-active]:!border-red-600',
                      '[&_.ant-pagination-item-active_a]:!text-white',
                      '[&_.ant-pagination-item-active]:shadow-md',
                      '[&_.ant-pagination-item-active]:shadow-red-500/30',
                      '[&_.ant-pagination-item-active]:dark:!bg-red-500',
                      '[&_.ant-pagination-item-active]:dark:!border-red-500',
                      '[&_.ant-pagination-item-active]:dark:shadow-red-500/20',
                      // Hover states
                      '[&_.ant-pagination-item:hover]:border-red-400',
                      '[&_.ant-pagination-item:hover]:dark:border-red-500',
                      '[&_.ant-pagination-item:hover_a]:text-red-600',
                      '[&_.ant-pagination-item:hover_a]:dark:text-red-400',
                      // Navigation buttons
                      '[&_.ant-pagination-prev]:text-gray-700',
                      '[&_.ant-pagination-prev]:dark:text-gray-300',
                      '[&_.ant-pagination-next]:text-gray-700',
                      '[&_.ant-pagination-next]:dark:text-gray-300',
                      '[&_.ant-pagination-prev:hover]:text-red-600',
                      '[&_.ant-pagination-prev:hover]:dark:text-red-400',
                      '[&_.ant-pagination-next:hover]:text-red-600',
                      '[&_.ant-pagination-next:hover]:dark:text-red-400'
                    )}
                  />
                </div>
              )}
            </>
          ) : searchQueryFailed ? (
            <EmptyCase text="No se encontraron preguntas con ese criterio de búsqueda" />
          ) : (
            <EmptyCase text="No hay preguntas fallidas" />
          )}
        </div>
      )
    }
  ];

  return (
    <Screen id="saved-questions" title="Preguntas destacadas">
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as 'saved' | 'failed');
          setCurrentPageSaved(1);
          setCurrentPageFailed(1);
        }}
        items={tabItems}
        className={clsx(
          '[&_.ant-tabs-nav]:mb-6',
          '[&_.ant-tabs-tab]:transition-all',
          '[&_.ant-tabs-tab-active]:text-blue-600',
          '[&_.ant-tabs-tab-active]:dark:text-blue-400',
          '[&_.ant-tabs-ink-bar]:bg-blue-600',
          '[&_.ant-tabs-ink-bar]:dark:bg-blue-400',
          '[&_.ant-tabs-tab]:text-gray-600',
          '[&_.ant-tabs-tab]:dark:text-gray-400',
          '[&_.ant-tabs-tab:hover]:text-blue-500',
          '[&_.ant-tabs-tab:hover]:dark:text-blue-300'
        )}
      />
    </Screen>
  );
};

export { SavedQuestionsPage };
